import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { 
  Shield, 
  Upload, 
  CheckCircle, 
  AlertCircle, 
  Clock, 
  X, 
  FileText, 
  Camera,
  User,
  AlertTriangle,
  Info
} from 'lucide-react';
import { authAPI } from '../services/api';
import { useThemeColors } from '../theme/ThemeContext';
import SEO from '../components/SEO';

const Verification = () => {
  const colors = useThemeColors();
  const [activeTab, setActiveTab] = useState('status');
  const [documentFront, setDocumentFront] = useState(null);
  const [documentBack, setDocumentBack] = useState(null);
  const [selfie, setSelfie] = useState(null);
  const [previewFront, setPreviewFront] = useState(null);
  const [previewBack, setPreviewBack] = useState(null);
  const [previewSelfie, setPreviewSelfie] = useState(null);

  const queryClient = useQueryClient();

  // Fetch verification status
  const {
    data: verificationStatus,
    isLoading: isLoadingStatus,
    refetch: refetchStatus,
  } = useQuery(
    'verificationStatus',
    authAPI.getVerificationStatus,
    {
      select: (response) => response.data.data,
    }
  );

  // Submit verification mutation
  const submitVerificationMutation = useMutation(authAPI.submitVerification, {
    onSuccess: () => {
      toast.success('Verification documents submitted successfully!');
      queryClient.invalidateQueries('verificationStatus');
      refetchStatus();
      setActiveTab('status');
      // Clear form
      setDocumentFront(null);
      setDocumentBack(null);
      setSelfie(null);
      setPreviewFront(null);
      setPreviewBack(null);
      setPreviewSelfie(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to submit verification');
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const handleFileChange = (fileType, file) => {
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }

      // Check file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Only JPG, PNG, and PDF files are allowed');
        return;
      }

      // Set file and preview
      switch (fileType) {
        case 'front':
          setDocumentFront(file);
          setPreviewFront(URL.createObjectURL(file));
          break;
        case 'back':
          setDocumentBack(file);
          setPreviewBack(URL.createObjectURL(file));
          break;
        case 'selfie':
          setSelfie(file);
          setPreviewSelfie(URL.createObjectURL(file));
          break;
      }
    }
  };

  const onSubmit = (data) => {
    if (!documentFront || !documentBack || !selfie) {
      toast.error('Please upload all required documents');
      return;
    }

    const formData = new FormData();
    formData.append('document_front', documentFront);
    formData.append('document_back', documentBack);
    formData.append('selfie', selfie);
    formData.append('document_type', data.documentType);
    formData.append('document_number', data.documentNumber);

    submitVerificationMutation.mutate(formData);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'verified':
        return 'bg-green-100 text-green-800';
      case 'partial':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
        return <CheckCircle className="h-5 w-5" />;
      case 'partial':
        return <AlertCircle className="h-5 w-5" />;
      case 'rejected':
        return <X className="h-5 w-5" />;
      case 'pending':
        return <Clock className="h-5 w-5" />;
      default:
        return <Shield className="h-5 w-5" />;
    }
  };

  const getStatusMessage = (status) => {
    switch (status) {
      case 'verified':
        return 'Your identity has been verified. You have full access to all platform features.';
      case 'partial':
        return 'Some documents have been verified. Please complete the verification process for full access.';
      case 'rejected':
        return 'Your verification was rejected. Please review the requirements and submit again.';
      case 'pending':
        return 'Your verification is being reviewed. This typically takes 1-3 business days.';
      default:
        return 'Complete verification to unlock all platform features and increase trust.';
    }
  };

  const canSubmit = !verificationStatus || verificationStatus.status === 'rejected' || verificationStatus.status === 'unverified';

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <SEO 
        title="Identity Verification" 
        description="Complete identity verification on TripTrot to unlock all platform features and increase trust with other users"
        keywords={['identity verification', 'account verification', 'kyc', 'trip trot verification']}
        canonical="/verification"
        noindex={true}
      />
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Identity Verification</h1>
        <p className="mt-2 text-gray-600">Verify your identity to unlock all platform features</p>
      </div>

      {/* Verification Status Card */}
      {verificationStatus && (
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 mb-8">
          <div className="flex items-start justify-between">
            <div className="flex items-start">
              <div className={`p-2 rounded-lg ${
                verificationStatus.status === 'verified' ? 'bg-green-100' :
                verificationStatus.status === 'partial' ? 'bg-yellow-100' :
                verificationStatus.status === 'rejected' ? 'bg-red-100' :
                'bg-blue-100'
              }`}>
                {getStatusIcon(verificationStatus.status)}
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-medium text-gray-900 capitalize">
                  {verificationStatus.status === 'unverified' ? 'Not Verified' : verificationStatus.status}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {getStatusMessage(verificationStatus.status)}
                </p>
                {verificationStatus.verified_at && (
                  <p className="text-xs text-gray-500 mt-2">
                    Verified on {new Date(verificationStatus.verified_at).toLocaleDateString()}
                  </p>
                )}
                {verificationStatus.rejection_reason && (
                  <div className="mt-2 p-2 bg-theme-error-50 border border-theme-error rounded">
                    <p className="text-sm text-theme-error">
                      <strong>Reason:</strong> {verificationStatus.rejection_reason}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(verificationStatus.status)}`}>
              {verificationStatus.status === 'unverified' ? 'Not Verified' : verificationStatus.status}
            </span>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-theme-light mb-6">
        <nav className="-mb-px flex space-x-8">
          {['status', 'submit'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-theme-primary text-theme-primary'
                  : 'border-transparent text-theme-secondary hover:text-theme-primary hover:border-theme-light'
              }`}
              disabled={tab === 'submit' && !canSubmit}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'status' && (
        <div className="space-y-6">
          {/* Verification Benefits */}
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Verification Benefits</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Full Platform Access</h4>
                  <p className="text-sm text-gray-600">Create parcels, trips, and access all features</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Increased Trust</h4>
                  <p className="text-sm text-gray-600">Verified users get more applications and trust</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Higher Limits</h4>
                  <p className="text-sm text-gray-600">Higher transaction and posting limits</p>
                </div>
              </div>
              <div className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Priority Support</h4>
                  <p className="text-sm text-gray-600">Faster resolution of disputes and issues</p>
                </div>
              </div>
            </div>
          </div>

          {/* Verification Requirements */}
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Verification Requirements</h3>
            <div className="space-y-4">
              <div className="flex items-start">
                <FileText className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Government-Issued ID</h4>
                  <p className="text-sm text-gray-600">
                    National ID, Passport, or Driver's License (front and back)
                  </p>
                  <ul className="mt-1 text-xs text-gray-500 list-disc list-inside">
                    <li>Clear, readable, and not expired</li>
                    <li>All corners visible</li>
                    <li>No glare or shadows</li>
                  </ul>
                </div>
              </div>
              <div className="flex items-start">
                <Camera className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="text-sm font-medium text-gray-900">Selfie Photo</h4>
                  <p className="text-sm text-gray-600">
                    Recent photo showing your face clearly
                  </p>
                  <ul className="mt-1 text-xs text-gray-500 list-disc list-inside">
                    <li>Face clearly visible</li>
                    <li>Good lighting</li>
                    <li>No filters or editing</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Processing Time */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-600 mt-0.5 mr-2" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Processing Time</p>
                <p className="mt-1">
                  Verification typically takes 1-3 business days. You'll receive an email once your verification is reviewed.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'submit' && (
        <div className="space-y-6">
          {/* Verification Form */}
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-6">Submit Verification Documents</h3>
            
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Document Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Document Type *
                  </label>
                  <select
                    {...register('documentType', { required: 'Document type is required' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select document type</option>
                    <option value="national_id">National ID</option>
                    <option value="passport">Passport</option>
                    <option value="driver_license">Driver's License</option>
                  </select>
                  {errors.documentType && (
                    <p className="mt-1 text-sm text-red-600">{errors.documentType.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Document Number *
                  </label>
                  <input
                    {...register('documentNumber', { required: 'Document number is required' })}
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter document number"
                  />
                  {errors.documentNumber && (
                    <p className="mt-1 text-sm text-red-600">{errors.documentNumber.message}</p>
                  )}
                </div>
              </div>

              {/* Document Upload */}
              <div className="space-y-4">
                <h4 className="text-sm font-medium text-gray-900">Upload Documents</h4>
                
                {/* Front of Document */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Front of Document *
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    {previewFront ? (
                      <div className="space-y-4">
                        <img
                          src={previewFront}
                          alt="Document front"
                          className="mx-auto h-32 w-auto object-contain"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setDocumentFront(null);
                            setPreviewFront(null);
                          }}
                          className="text-red-600 hover:text-red-700 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div>
                        <Upload className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="mt-2">
                          <label className="cursor-pointer">
                            <span className="text-blue-600 hover:text-blue-500">Upload front side</span>
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              onChange={(e) => handleFileChange('front', e.target.files[0])}
                              className="hidden"
                            />
                          </label>
                          <p className="text-xs text-gray-500 mt-1">
                            JPG, PNG, or PDF (max 5MB)
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Back of Document */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Back of Document *
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    {previewBack ? (
                      <div className="space-y-4">
                        <img
                          src={previewBack}
                          alt="Document back"
                          className="mx-auto h-32 w-auto object-contain"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setDocumentBack(null);
                            setPreviewBack(null);
                          }}
                          className="text-red-600 hover:text-red-700 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div>
                        <Upload className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="mt-2">
                          <label className="cursor-pointer">
                            <span className="text-blue-600 hover:text-blue-500">Upload back side</span>
                            <input
                              type="file"
                              accept="image/*,.pdf"
                              onChange={(e) => handleFileChange('back', e.target.files[0])}
                              className="hidden"
                            />
                          </label>
                          <p className="text-xs text-gray-500 mt-1">
                            JPG, PNG, or PDF (max 5MB)
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Selfie */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Selfie Photo *
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    {previewSelfie ? (
                      <div className="space-y-4">
                        <img
                          src={previewSelfie}
                          alt="Selfie"
                          className="mx-auto h-32 w-32 object-cover rounded-full"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setSelfie(null);
                            setPreviewSelfie(null);
                          }}
                          className="text-red-600 hover:text-red-700 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div>
                        <Camera className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="mt-2">
                          <label className="cursor-pointer">
                            <span className="text-blue-600 hover:text-blue-500">Upload selfie</span>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFileChange('selfie', e.target.files[0])}
                              className="hidden"
                            />
                          </label>
                          <p className="text-xs text-gray-500 mt-1">
                            JPG or PNG (max 5MB)
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Warning */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
                <div className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 mr-2" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium">Important:</p>
                    <ul className="mt-1 list-disc list-inside space-y-1">
                      <li>All documents must be clear and readable</li>
                      <li>Documents must be current and not expired</li>
                      <li>Submitting false documents will result in permanent ban</li>
                      <li>Verification can take 1-3 business days</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={submitVerificationMutation.isLoading}
                  className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitVerificationMutation.isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Shield className="h-4 w-4 mr-2" />
                      Submit for Verification
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Verification;
